import React, { useEffect, useRef } from 'react';
import SwaggerUIBundle from 'swagger-ui-dist/swagger-ui-bundle';
import SwaggerUIStandalonePreset from 'swagger-ui-dist/swagger-ui-standalone-preset';
import 'swagger-ui-dist/swagger-ui.css';
import { Layout } from '../components/Layout';

export function ApiDocs() {
  const baseUrl = process.env.API_URL || 'http://localhost:8080';
  const apiUrl = `${baseUrl}/api/openapi.json`;
  const swaggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (swaggerRef.current) {
      SwaggerUIBundle({
        url: apiUrl,
        dom_id: '#swagger-ui',
        presets: [
          SwaggerUIBundle.presets.apis,
          SwaggerUIStandalonePreset
        ],
        layout: 'StandaloneLayout',
        docExpansion: 'list',
        defaultModelsExpandDepth: 1,
        defaultModelExpandDepth: 3,
        displayRequestDuration: true,
        filter: true,
        showExtensions: true,
        showCommonExtensions: true,
      });
    }
  }, [apiUrl]);

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-6">
        <div className="container-custom">
          <h1 className="text-2xl font-bold mb-1">API Documentation</h1>
          <p className="text-sm text-primary-100">
            REST API for order management and account queries
          </p>
        </div>
      </section>

      {/* Auth + Quick Reference row */}
      <section className="bg-white border-b">
        <div className="container-custom py-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Authentication */}
            <div className="lg:col-span-1 bg-primary-50 border border-primary-200 rounded-md p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Authentication</h2>
              <div className="space-y-2 text-xs text-gray-700">
                <p>
                  Pass an API key via{' '}
                  <code className="bg-white px-1 py-0.5 rounded border text-[11px]">Authorization</code>{' '}
                  header:
                </p>
                <div className="bg-white rounded border border-primary-200 px-2.5 py-1.5 font-mono text-[11px] overflow-x-auto">
                  Authorization: Bearer aok_your-key
                </div>
                <ul className="list-disc list-inside space-y-0.5 text-[11px] text-gray-600">
                  <li>Scoped to a single trading account</li>
                  <li>Format: <code className="bg-white px-1 rounded border text-[11px]">aok_&lt;uuid&gt;</code></li>
                  <li>Shown once at creation only</li>
                </ul>
                <p className="text-[11px] text-gray-500 pt-1">
                  <strong>Swagger UI:</strong> Click "Authorize" below and enter{' '}
                  <code className="bg-white px-1 rounded border text-[11px]">Bearer aok_your-key</code>
                </p>
              </div>
            </div>

            {/* Endpoint quick reference */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-md p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wide">Order Management</h3>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  <li className="flex items-center">
                    <span className="text-green-600 font-mono text-[11px] font-bold w-12 shrink-0">POST</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">/api/orders</code>
                    <span className="ml-auto text-gray-400 text-[11px]">Submit</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-orange-600 font-mono text-[11px] font-bold w-12 shrink-0">PATCH</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">/api/orders/:id</code>
                    <span className="ml-auto text-gray-400 text-[11px]">Amend</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-red-600 font-mono text-[11px] font-bold w-12 shrink-0">DELETE</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">/api/orders/:id</code>
                    <span className="ml-auto text-gray-400 text-[11px]">Cancel</span>
                  </li>
                </ul>
              </div>

              <div className="border rounded-md p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wide">Account & Query</h3>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  <li className="flex items-center">
                    <span className="text-blue-600 font-mono text-[11px] font-bold w-12 shrink-0">GET</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">/api/accounts/:id</code>
                    <span className="ml-auto text-gray-400 text-[11px]">Details</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-600 font-mono text-[11px] font-bold w-12 shrink-0">GET</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">/api/accounts/:id/orders</code>
                    <span className="ml-auto text-gray-400 text-[11px]">List</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-600 font-mono text-[11px] font-bold w-12 shrink-0">GET</span>
                    <code className="text-[11px] bg-gray-50 px-1.5 py-0.5 rounded">…/orders/:client_id</code>
                    <span className="ml-auto text-gray-400 text-[11px]">By ID</span>
                  </li>
                </ul>
              </div>

              <div className="sm:col-span-2 border rounded-md p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-1.5 uppercase tracking-wide">Error Format</h3>
                <div className="bg-gray-50 rounded border px-2.5 py-1.5 text-[11px] font-mono overflow-x-auto text-gray-600">
                  {`{ "error": "message", "details": "optional context" }`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Swagger UI */}
      <section className="bg-white">
        <div className="container-custom py-5">
          <div className="swagger-container swagger-compact">
            <div id="swagger-ui" ref={swaggerRef}></div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
